// src/pages/ReportsPage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import LoadingIndicator from "../components/LoadingIndicator";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaEye,
  FaEyeSlash,
  FaFilter,
  FaFileAlt,
  FaTimes,
  FaClock,
  FaTag,
  FaCommentAlt,
  FaUser,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes as FaTimesCircle,
  FaDownload,
  FaPrint,
  FaEnvelope,
  FaSpinner,
  FaTimesCircle as FaRemoveFilter,
} from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const PAGE_SIZE = 20;
// Minnescache som lever kvar vid sidnavigation men töms vid omladdning.
const reportsPageCache = new Map();
const pendingReads = new Map();
function readCollectionOnce(key, reference) {
  if (!pendingReads.has(key)) {
    pendingReads.set(key, getDocs(reference).finally(() => pendingReads.delete(key)));
  }
  return pendingReads.get(key);
}

// Hjälpfunktion för att formatera datum och tid
const formatDateTime = (date) => {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  return (
    d.toLocaleDateString("sv-SE") +
    " • " +
    d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })
  );
};

// Hjälpfunktion för att formatera datum
const formatDate = (date) => {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("sv-SE");
};

// Huvudkomponent för rapportöversikt
export default function ReportsPage() {
  const { reportsEnabled, reportsLoading, activeCompany } = useCompany();

  if (reportsLoading) return <LoadingIndicator message="Laddar företag..." fullPage />;
  if (!reportsEnabled) return <Navigate to="/dashboard" replace />;

  return <ReportsContent key={activeCompany.companyAccountUid} />;
}

function ReportsContent() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  const cacheKey = `${currentUser?.uid || ""}:${activeCompany?.companyAccountUid || ""}`;
  if (!reportsPageCache.has(cacheKey)) reportsPageCache.set(cacheKey, {});
  const cache = reportsPageCache.get(cacheKey);
  const reportRequest = useRef(0);

  // State för UI
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [loading, setLoading] = useState(true);
  const [categoriesReady, setCategoriesReady] = useState(Boolean(cache.categories));
  const [employeesReady, setEmployeesReady] = useState(Boolean(cache.employees));
  
  // State för filtrering
  const [selectedMonth, setSelectedMonth] = useState(cache.month ?? new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(cache.year ?? new Date().getFullYear());
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [readFilter, setReadFilter] = useState("all"); // "all", "read", "unread"
  const [page, setPage] = useState(1);
  
  // State för data
  const [reports, setReports] = useState(cache.reports || []);
  const [categories, setCategories] = useState(cache.categories || []);
  const [employees, setEmployees] = useState(cache.employees || []);
  const [summary, setSummary] = useState({
    totalReports: 0,
    readReports: 0,
    unreadReports: 0,
  });
  
  // State för popup-fönster
  const [selectedReport, setSelectedReport] = useState(null);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Hämta companyAccountUid från activeCompany
  const companyAccountUid = activeCompany?.companyAccountUid;

  // Lista över månader på svenska
  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  // Hämta kategorier från Firestore - använder companyAccountUid
  const fetchCategories = async () => {
    if (!companyAccountUid) return;

    try {
      const categoriesRef = collection(
        db,
        "users",
        companyAccountUid,
        "reportCategories"
      );

      const querySnapshot = await readCollectionOnce(`${cacheKey}:categories`, categoriesRef);
      const categoriesData = querySnapshot.docs.map((categoryDoc) => ({
        id: categoryDoc.id,
        ...categoryDoc.data(),
      }));

      cache.categories = categoriesData;
      setCategories(categoriesData);
    } catch (err) {
      console.error("❌ Kunde inte ladda kategorier", err);
    } finally {
      setCategoriesReady(true);
    }
  };

  // Hämta anställda från Firestore - använder companyAccountUid
  const fetchEmployees = async () => {
    if (!companyAccountUid) return;

    try {
      const employedRef = collection(db, "users", companyAccountUid, "employed");

      const snapshot = await readCollectionOnce(`${cacheKey}:employees`, employedRef);
      const users = [];

      snapshot.forEach((userDoc) => {
        const userData = userDoc.data();

        let fullName = userData.name || "";
        if (userData.firstName && userData.lastName) {
          fullName = `${userData.firstName} ${userData.lastName}`;
        } else if (userData.firstName) {
          fullName = userData.firstName;
        } else if (userData.lastName) {
          fullName = userData.lastName;
        } else if (userData.displayName) {
          fullName = userData.displayName;
        }

        users.push({
          id: userDoc.id,
          ...userData,
          name: fullName || "Användare",
        });
      });

      const sortedUsers = users.sort((a, b) => {
        return (a.name || "").localeCompare(b.name || "");
      });

      cache.employees = sortedUsers;
      setEmployees(sortedUsers);
      setEmployeesReady(true);
    } catch (err) {
      console.error("❌ Kunde inte ladda anställda", err);
      setEmployeesReady(true);
    }
  };

  // Hämta rapporter från Firestore - använder companyAccountUid
  const fetchReports = async () => {
    if (!companyAccountUid) return;

    const request = ++reportRequest.current;
    if (cache.reports && cache.month === selectedMonth && cache.year === selectedYear) {
      setReports(cache.reports);
      updateSummary(cache.reports);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const reportsRef = collection(db, "users", companyAccountUid, "reports");
      const querySnapshot = await readCollectionOnce(`${cacheKey}:reports:${selectedYear}:${selectedMonth}`, reportsRef);

      if (request !== reportRequest.current) return;
      const reportsData = querySnapshot.docs.map((reportDoc) => ({
        id: reportDoc.id,
        ...reportDoc.data(),
        createdAt: reportDoc.data().createdAt?.toDate?.() || new Date(),
        startTime: reportDoc.data().startTime?.toDate?.() || new Date(),
        endTime: reportDoc.data().endTime?.toDate?.() || new Date(),
      }));

      const filteredByDate = reportsData.filter((report) => {
        const date = report.createdAt || report.startTime;
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return month === selectedMonth && year === selectedYear;
      });

      cache.reports = filteredByDate;
      cache.month = selectedMonth;
      cache.year = selectedYear;
      setReports(filteredByDate);
      updateSummary(filteredByDate);
    } catch (err) {
      console.error("❌ Kunde inte ladda rapporter", err);
    } finally {
      if (request === reportRequest.current) setLoading(false);
    }
  };

  // Ladda kategorier och anställda vid mount
  useEffect(() => {
    if (companyAccountUid) {
      if (!cache.categories) fetchCategories();
      if (!cache.employees) fetchEmployees();
    }
  }, [companyAccountUid]);

  // Ladda rapporter när månad, år ändras
  useEffect(() => {
    if (companyAccountUid) {
      fetchReports();
    }
  }, [selectedMonth, selectedYear, companyAccountUid]);

  useEffect(() => {
    if (!loading) {
      cache.reports = reports;
      updateSummary(reports);
    }
  }, [reports, loading]);

  // Auto-hide toast efter 3 sekunder
  useEffect(() => {
    if (!toastMessage) return;

    const timer = setTimeout(() => {
      setToastMessage("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [toastMessage]);

  // Uppdatera sammanställning
  const updateSummary = (data) => {
    const readCount = data.filter((r) => r.isRead).length;
    setSummary({
      totalReports: data.length,
      readReports: readCount,
      unreadReports: data.length - readCount,
    });
  };

  // Filtrera data baserat på valda filter
  const filteredData = useMemo(() => {
    return reports.filter((row) => {
      if (selectedCategory && row.categoryId !== selectedCategory) return false;
      if (selectedEmployee && row.employeeId !== selectedEmployee) return false;
      if (readFilter === "read" && !row.isRead) return false;
      if (readFilter === "unread" && row.isRead) return false;
      return true;
    });
  }, [reports, selectedCategory, selectedEmployee, readFilter]);

  // Paginering
  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1;
  const pagedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Återställ sida när filter ändras
  useEffect(() => {
    setPage(1);
  }, [filteredData, selectedCategory, selectedEmployee, readFilter]);

  // Hantera markera som läst/oläst
  const handleToggleReadStatus = async (reportId, currentStatus, event) => {
    event.stopPropagation();

    if (!companyAccountUid) return;

    try {
      const reportRef = doc(db, "users", companyAccountUid, "reports", reportId);

      await updateDoc(reportRef, {
        isRead: !currentStatus,
      });

      setReports((prevReports) =>
        prevReports.map((report) =>
          report.id === reportId
            ? { ...report, isRead: !currentStatus }
            : report
        )
      );

      setToastType("success");
      setToastMessage(
        `Rapport markerad som ${!currentStatus ? "läst" : "oläst"}`
      );
    } catch (error) {
      console.error(error);
      setToastType("error");
      setToastMessage("Kunde inte uppdatera status.");
    }
  };

  // Radera rapport
  const handleDeleteReport = async (reportId, event) => {
    if (event) event.stopPropagation();
    
    if (!companyAccountUid) return;

    if (!window.confirm("Är du säker på att du vill radera denna rapport? Detta kan inte ångras.")) {
      return;
    }

    try {
      const reportRef = doc(db, "users", companyAccountUid, "reports", reportId);
      await deleteDoc(reportRef);

      // Uppdatera state
      setReports((prevReports) => prevReports.filter((report) => report.id !== reportId));
      setSelectedReport(null); // Stäng popupen
      
      setToastType("success");
      setToastMessage("Rapport raderad");
    } catch (error) {
      console.error(error);
      setToastType("error");
      setToastMessage("Kunde inte radera rapport.");
    }
  };

  // Rensa alla filter
  const clearAllFilters = () => {
    setSelectedCategory("");
    setSelectedEmployee("");
    setReadFilter("all");
    setPage(1);
    setToastType("success");
    setToastMessage("Alla filter har rensats");
  };

  // Rensa kategori-filter
  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setPage(1);
    setToastType("success");
    setToastMessage("Kategorifilter rensat");
  };

  // Rensa anställd-filter
  const clearEmployeeFilter = () => {
    setSelectedEmployee("");
    setPage(1);
    setToastType("success");
    setToastMessage("Anställd-filter rensat");
  };

  // Öppna popup med rapportdetaljer
  const handleRowClick = (report) => {
    setSelectedReport(report);
  };

  // Stäng popup
  const handleClosePopup = () => {
    setSelectedReport(null);
  };

  // Lägg till kategori
  const handleAddCategory = async () => {
    if (!newCategory.trim() || !companyAccountUid) return;

    try {
      const categoriesRef = collection(
        db,
        "users",
        companyAccountUid,
        "reportCategories"
      );

      await addDoc(categoriesRef, {
        name: newCategory.trim(),
        createdAt: new Date(),
      });

      setNewCategory("");
      fetchCategories();
      setToastType("success");
      setToastMessage("Kategori tillagd");
    } catch (err) {
      console.error(err);
      setToastType("error");
      setToastMessage("Kunde inte lägga till kategori");
    }
  };

  // Redigera kategori
  const handleEditCategory = async (categoryId, newName) => {
    if (!companyAccountUid || !newName.trim()) return;

    try {
      const categoryRef = doc(
        db,
        "users",
        companyAccountUid,
        "reportCategories",
        categoryId
      );

      await updateDoc(categoryRef, {
        name: newName.trim(),
      });

      fetchCategories();
      setEditingCategory(null);
      setEditedCategoryName("");
      setToastType("success");
      setToastMessage("Kategori uppdaterad");
    } catch (err) {
      console.error(err);
      setToastType("error");
      setToastMessage("Kunde inte uppdatera kategori");
    }
  };

  // Ta bort kategori
  const handleDeleteCategory = async () => {
    if (!companyAccountUid || !categoryToDelete?.id) return;

    try {
      const categoryRef = doc(
        db,
        "users",
        companyAccountUid,
        "reportCategories",
        categoryToDelete.id
      );

      await deleteDoc(categoryRef);

      setShowDeletePopup(false);
      setCategoryToDelete(null);
      setSelectedCategory("");
      fetchCategories();
      setToastType("success");
      setToastMessage("Kategori borttagen");
    } catch (err) {
      console.error(err);
      setToastType("error");
      setToastMessage("Kunde inte ta bort kategori");
    }
  };

  // Exportera rapport (simulerad)
  const handleExportReport = (report, format) => {
    setGeneratingReport(true);

    setTimeout(() => {
      setGeneratingReport(false);
      const employeeName = report.employeeName || getEmployeeName(report.employeeId);
      alert(`Exporterar ${employeeName} som ${format.toUpperCase()}`);
    }, 1500);
  };

  // Skicka via e-post (simulerad)
  const handleEmailReport = (report) => {
    const employeeName = report.employeeName || getEmployeeName(report.employeeId);
    alert(`Skickar rapport för ${employeeName} via e-post`);
  };

  // Skapa ny rapport (simulerad)
  const handleCreateReport = () => {
    alert("Funktion för att skapa ny rapport kommer snart!");
  };

  // Hjälpfunktioner för att hämta namn
  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "Okänd kategori";
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || "Okänd anställd";
  };

  // Uppdaterad funktion för att visa anställds namn - först från rapporten, sedan från employees listan
  const getDisplayEmployeeName = (report) => {
    if (report.employeeName && report.employeeName !== "Användare") {
      return report.employeeName;
    }
    return getEmployeeName(report.employeeId);
  };

  const getCategoryStyle = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return { background: "#e5e7eb", color: "#374151" };
    return { background: "#e5e7eb", color: "#374151" };
  };

  // Popup-hanterare
  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeletePopup(true);
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  const startEditing = (category) => {
    setEditingCategory(category.id);
    setEditedCategoryName(category.name);
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    setEditedCategoryName("");
  };

  const handleOpenCategoryPopup = () => {
    setShowCategoryPopup(true);
    setNewCategory("");
    setEditingCategory(null);
    setEditedCategoryName("");
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  const handleCloseCategoryPopup = () => {
    setShowCategoryPopup(false);
    setNewCategory("");
    setEditingCategory(null);
    setEditedCategoryName("");
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  // Hämta vald kategoris namn för visning
  const getSelectedCategoryName = () => {
    if (!selectedCategory) return null;
    const category = categories.find(c => c.id === selectedCategory);
    return category?.name || selectedCategory;
  };

  // Hämta vald anställds namn för visning
  const getSelectedEmployeeName = () => {
    if (!selectedEmployee) return null;
    const employee = employees.find(e => e.id === selectedEmployee);
    return employee?.name || selectedEmployee;
  };

  // Hämta read filter text
  const getReadFilterText = () => {
    if (readFilter === "read") return "Lästa";
    if (readFilter === "unread") return "Olästa";
    return null;
  };

  // Visa laddningsindikator medan företag laddas
  if (companyLoading || loading || !categoriesReady || !employeesReady) {
    return <LoadingIndicator message="Laddar rapporter..." fullPage />;
  }

  // Visa meddelande om inget företag är valt
  if (!activeCompany) {
    return (
      <div style={styles.wrap}>
        <div style={styles.container}>
          <div style={styles.emptyState}>
            <p>Inget företag valt. Vänligen logga in igen.</p>
            <button
              onClick={() => window.location.href = "/login"}
              style={{
                padding: "10px 20px",
                background: "#6323ef",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                marginTop: 16,
              }}
            >
              Gå till inloggning
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Stilar för komponenten
  const styles = {
    wrap: {
      width: "100%",
      background: "#f8fafc",
      minHeight: "100vh",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    container: { margin: "0 auto", padding: "24px 32px", maxWidth: 1600 },

    header: { marginBottom: 32 },
    pageTitle: {
      margin: 0,
      fontSize: 32,
      fontWeight: 700,
      color: "#1a1a1a",
      letterSpacing: "-0.02em",
    },
    pageSubtitle: {
      margin: "4px 0 0",
      fontSize: 16,
      color: "#64748b",
      fontWeight: 400,
    },

    statsContainer: {
      display: "flex",
      gap: 16,
      flexWrap: "wrap",
      marginBottom: 24,
    },
    statCard: {
      background: "white",
      padding: "20px 24px",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
      flex: 1,
      minWidth: 200,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    statValue: {
      fontSize: 32,
      fontWeight: 700,
      color: "#1a1a1a",
      lineHeight: 1.2,
    },
    statLabel: {
      fontSize: 13,
      color: "#64748b",
      fontWeight: 500,
      marginBottom: 8,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    statIcon: { fontSize: 24, color: "#2563eb", marginBottom: 12 },

    controlsSection: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 24,
      gap: 16,
      flexWrap: "wrap",
      background: "white",
      padding: "20px",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    dateSelector: { display: "flex", alignItems: "center", gap: 12 },
    select: {
      background: "white",
      border: "1px solid #d1d5db",
      color: "#374151",
      padding: "8px 12px",
      borderRadius: 6,
      outline: "none",
      fontSize: 14,
      fontWeight: 500,
      minWidth: 140,
    },
    filterGroup: {
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "center",
    },

    categoryFilterWrapper: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },

    manageCategoriesButton: {
      background: "#f1f5f9",
      border: "1px dashed #94a3b8",
      color: "#64748b",
      padding: "8px 12px",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },

    readFilterGroup: {
      display: "flex",
      gap: 8,
      background: "#f1f5f9",
      padding: "4px",
      borderRadius: 8,
    },
    readFilterButton: (active) => ({
      padding: "6px 12px",
      borderRadius: 6,
      border: "none",
      background: active ? "white" : "transparent",
      color: active ? "#2563eb" : "#64748b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
      boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
    }),

    activeFiltersContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: 16,
      alignItems: "center",
    },
    filterBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 500,
      backgroundColor: "#f1f5f9",
      color: "#1e293b",
    },
    filterBadgeRemove: {
      cursor: "pointer",
      color: "#64748b",
      display: "flex",
      alignItems: "center",
      fontSize: 12,
      transition: "color 0.2s",
    },
    clearAllButton: {
      background: "none",
      border: "none",
      color: "#ef4444",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      padding: "6px 12px",
      borderRadius: 20,
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s",
    },

    tableContainer: {
      background: "white",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
      marginBottom: 24,
    },
    tableHeader: {
      display: "grid",
      gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1.2fr 1.2fr 1.5fr",
      gap: 16,
      padding: "14px 20px",
      color: "#374151",
      fontSize: 12,
      fontWeight: 700,
      background: "#f8fafc",
      borderBottom: "1px solid #e5e7eb",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: "1.5fr 1.5fr 1.5fr 1.2fr 1.2fr 1.5fr",
      gap: 16,
      alignItems: "center",
      padding: "16px 20px",
      borderBottom: "1px solid #f3f4f6",
      fontSize: 14,
      transition: "background-color 0.12s ease",
      background: "white",
      cursor: "pointer",
    },
    emptyState: {
      padding: "48px 20px",
      color: "#6b7280",
      fontSize: 14,
      textAlign: "center",
    },

    cell: {
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    cellRight: { textAlign: "right" },

    categoryBadge: {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
    },

    readStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
    },
    readStatusRead: {
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    },
    readStatusUnread: {
      background: "#fef2f2",
      color: "#991b1b",
      border: "1px solid #fecaca",
    },

    actionButton: {
      padding: "6px 12px",
      borderRadius: 6,
      border: "none",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s",
    },
    markReadButton: {
      background: "#2563eb",
      color: "white",
    },
    markUnreadButton: {
      background: "#f1f5f9",
      color: "#64748b",
    },

    pagination: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    paginationInfo: { fontSize: 14, color: "#6b7280", fontWeight: 500 },
    paginationControls: { display: "flex", alignItems: "center", gap: 8 },
    pageBtn: {
      borderRadius: 6,
      border: "1px solid #d1d5db",
      background: "white",
      color: "#374151",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      height: 32,
      minWidth: 100,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },

    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
    },

    popup: {
      background: "white",
      borderRadius: 16,
      width: "90%",
      maxWidth: 600,
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow:
        "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    },
    popupHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px 24px",
      borderBottom: "1px solid #e5e7eb",
    },
    popupTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: "#1a1a1a",
      margin: 0,
    },
    closeButton: {
      background: "transparent",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      color: "#9ca3af",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 6,
    },
    popupContent: {
      padding: "24px",
    },

    addCategorySection: {
      marginBottom: 32,
      padding: "20px",
      background: "#f8fafc",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#1a1a1a",
      margin: "0 0 16px 0",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    formGroup: {
      display: "flex",
      gap: 12,
      marginBottom: 0,
    },
    input: {
      flex: 1,
      padding: "10px 14px",
      fontSize: 15,
      border: "1px solid #d1d5db",
      borderRadius: 8,
      outline: "none",
      transition: "border-color 0.2s",
    },
    addButton: {
      padding: "10px 20px",
      background: "#2563eb",
      border: "none",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      color: "white",
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
    },

    categoryList: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    categoryItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      transition: "all 0.2s",
    },
    categoryInfo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flex: 1,
    },
    categoryName: {
      fontSize: 15,
      fontWeight: 500,
      color: "#1a1a1a",
    },
    categoryPreview: {
      padding: "4px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
    },
    categoryActions: {
      display: "flex",
      gap: 8,
    },
    iconButton: {
      background: "transparent",
      border: "none",
      padding: "8px",
      borderRadius: 6,
      cursor: "pointer",
      color: "#64748b",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    editButton: {},
    deleteButton: {},
    saveButton: {
      padding: "8px 16px",
      background: "#2563eb",
      border: "none",
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    cancelButton: {
      padding: "8px 16px",
      background: "#f1f5f9",
      border: "1px solid #e2e8f0",
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "#64748b",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },

    editInput: {
      flex: 1,
      padding: "8px 12px",
      fontSize: 14,
      border: "2px solid #2563eb",
      borderRadius: 6,
      outline: "none",
    },

    simplePopup: {
      background: "white",
      borderRadius: 12,
      width: "90%",
      maxWidth: 300,
      padding: "20px",
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
    },
    simplePopupText: {
      fontSize: 16,
      color: "#1a1a1a",
      marginBottom: 20,
      textAlign: "center",
    },
    simplePopupButtons: {
      display: "flex",
      gap: 10,
      justifyContent: "center",
    },
    simpleCancelButton: {
      padding: "8px 16px",
      background: "#f3f4f6",
      border: "1px solid #e5e7eb",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "#4b5563",
      cursor: "pointer",
      flex: 1,
    },
    simpleDeleteButton: {
      padding: "8px 16px",
      background: "#dc2626",
      border: "none",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "white",
      cursor: "pointer",
      flex: 1,
    },

    infoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 20,
      marginBottom: 24,
    },
    infoItem: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    infoLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    infoValue: {
      fontSize: 16,
      fontWeight: 600,
      color: "#1a1a1a",
    },
    notesSection: {
      background: "#f9fafb",
      borderRadius: 12,
      padding: "16px",
      marginTop: 8,
    },
    notesLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 12,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    notesContent: {
      fontSize: 15,
      color: "#1f2937",
      lineHeight: 1.6,
      whiteSpace: "pre-wrap",
    },

    exportButtons: {
      display: "flex",
      gap: 8,
      marginTop: 16,
      justifyContent: "flex-end",
      flexWrap: "wrap",
    },
    exportButton: {
      padding: "8px 16px",
      borderRadius: 6,
      border: "1px solid #e2e8f0",
      background: "white",
      color: "#4b5563",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    deleteButtonRed: {
      background: "#dc2626",
      color: "white",
      border: "none",
    },
    createButton: {
      background: "linear-gradient(135deg, #8d43ff, #6323ef)",
      color: "white",
      padding: "12px 24px",
      borderRadius: 8,
      border: "none",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
  };

  // Kontrollera om något filter är aktivt
  const hasActiveFilters = selectedCategory || selectedEmployee || readFilter !== "all";

  return (
    <div style={styles.wrap}>
      <div style={styles.container}>
        {/* Sidhuvud med företagsnamn */}
        <div style={styles.header}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div>
              <h1 style={styles.pageTitle}>Rapporter</h1>
              <p style={styles.pageSubtitle}>
                Se vilka rapporter som är lästa och olästa för <strong>{activeCompany?.companyName}</strong>
              </p>
            </div>
          
          </div>
        </div>

        {/* Statistik-kort */}
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <FaFileAlt style={styles.statIcon} />
            <div style={styles.statLabel}>Totalt rapporter</div>
            <div style={styles.statValue}>{summary.totalReports}</div>
          </div>
          <div style={styles.statCard}>
            <FaCheckCircle style={{ ...styles.statIcon, color: "#10b981" }} />
            <div style={styles.statLabel}>Lästa</div>
            <div style={styles.statValue}>{summary.readReports}</div>
          </div>
          <div style={styles.statCard}>
            <FaHourglassHalf style={{ ...styles.statIcon, color: "#f59e0b" }} />
            <div style={styles.statLabel}>Olästa</div>
            <div style={styles.statValue}>{summary.unreadReports}</div>
          </div>
        </div>

        {/* Filterkontroller */}
        <div style={styles.controlsSection}>
          <div style={styles.dateSelector}>
            <FaCalendarAlt style={{ color: "#64748b" }} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(+e.target.value)}
              style={styles.select}
            >
              {monthsInSwedish.map((m, i) => (
                <option key={i} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(+e.target.value)}
              style={styles.select}
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <div style={styles.categoryFilterWrapper}>
              <button style={styles.manageCategoriesButton} onClick={handleOpenCategoryPopup}>
                <FaEdit /> Hantera kategorier
              </button>
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={styles.select}
            >
              <option value="">Alla kategorier</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              style={styles.select}
            >
              <option value="">Alla anställda</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>

            <div style={styles.readFilterGroup}>
              <button
                style={styles.readFilterButton(readFilter === "all")}
                onClick={() => setReadFilter("all")}
              >
                <FaFilter /> Alla
              </button>
              <button
                style={styles.readFilterButton(readFilter === "unread")}
                onClick={() => setReadFilter("unread")}
              >
                <FaHourglassHalf /> Olästa
              </button>
              <button
                style={styles.readFilterButton(readFilter === "read")}
                onClick={() => setReadFilter("read")}
              >
                <FaCheckCircle /> Lästa
              </button>
            </div>
          </div>
        </div>

        {/* VISUALISERING AV AKTIVA FILTER */}
        {hasActiveFilters && (
          <div style={styles.activeFiltersContainer}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              Aktiva filter:
            </span>
            
            {selectedCategory && (
              <div style={styles.filterBadge}>
                <FaTag size={12} />
                {getSelectedCategoryName()}
                <span
                  style={styles.filterBadgeRemove}
                  onClick={clearCategoryFilter}
                  title="Ta bort filter"
                >
                  <FaRemoveFilter />
                </span>
              </div>
            )}
            
            {selectedEmployee && (
              <div style={styles.filterBadge}>
                <FaUser size={12} />
                {getSelectedEmployeeName()}
                <span
                  style={styles.filterBadgeRemove}
                  onClick={clearEmployeeFilter}
                  title="Ta bort filter"
                >
                  <FaRemoveFilter />
                </span>
              </div>
            )}
            
            {readFilter !== "all" && (
              <div style={styles.filterBadge}>
                {readFilter === "read" ? <FaCheckCircle size={12} /> : <FaHourglassHalf size={12} />}
                {getReadFilterText()}
                <span
                  style={styles.filterBadgeRemove}
                  onClick={() => setReadFilter("all")}
                  title="Ta bort filter"
                >
                  <FaRemoveFilter />
                </span>
              </div>
            )}
            
          
          </div>
        )}

        {/* Tabell med rapporter */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
            <span>Anställd</span>
            <span>Inskickad</span>
            <span>Projekt</span>
            <span>Kategori</span>
            <span>Status</span>
            <span style={styles.cellRight}>Åtgärd</span>
          </div>

          {loading ? (
            <LoadingIndicator message="Laddar rapportdata..." />
          ) : pagedData.length === 0 ? (
            <div style={styles.emptyState}>
              {selectedCategory || selectedEmployee || readFilter !== "all"
                ? "Ingen data matchar dina filter."
                : "Inga rapporter tillgängliga för vald period."}
            </div>
          ) : (
            pagedData.map((row) => (
              <div key={row.id} style={styles.tableRow} onClick={() => handleRowClick(row)}>
                <span style={styles.cell}>{getDisplayEmployeeName(row)}</span>
                <span style={styles.cell}>
                  <div>{formatDateTime(row.createdAt)}</div>
                </span>
                <span style={styles.cell}>{row.projectName || "-"}</span>
                <span style={styles.cell}>
                  <span
                    style={{
                      ...styles.categoryBadge,
                      ...getCategoryStyle(row.categoryId),
                    }}
                  >
                    {getCategoryName(row.categoryId)}
                  </span>
                </span>
                <span style={styles.cell}>
                  <div
                    style={{
                      ...styles.readStatus,
                      ...(row.isRead ? styles.readStatusRead : styles.readStatusUnread),
                    }}
                  >
                    {row.isRead ? <FaCheckCircle /> : <FaHourglassHalf />}
                    {row.isRead ? "Läst" : "Oläst"}
                  </div>
                </span>
                <span style={{ ...styles.cell, ...styles.cellRight }}>
                  <button
                    style={{
                      ...styles.actionButton,
                      ...(row.isRead ? styles.markUnreadButton : styles.markReadButton),
                    }}
                    onClick={(e) => handleToggleReadStatus(row.id, row.isRead, e)}
                  >
                    {row.isRead ? <FaEyeSlash /> : <FaEye />}
                    {row.isRead ? "Markera oläst" : "Markera läst"}
                  </button>
                </span>
              </div>
            ))
          )}
        </div>

        {/* Paginering */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Visar {pagedData.length} av {filteredData.length} rapporter
            </div>
            <div style={styles.paginationControls}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  ...styles.pageBtn,
                  opacity: page === 1 ? 0.5 : 1,
                  cursor: page === 1 ? "not-allowed" : "pointer",
                }}
              >
                Föregående
              </button>
              <span style={{ fontSize: 14, color: "#6b7280", padding: "0 16px" }}>
                Sida {page} av {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  ...styles.pageBtn,
                  opacity: page === totalPages ? 0.5 : 1,
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Nästa
              </button>
            </div>
          </div>
        )}

        {/* Kategori-hanteringspopup */}
        {showCategoryPopup && (
          <div style={styles.overlay} onClick={handleCloseCategoryPopup}>
            <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.popupHeader}>
                <h2 style={styles.popupTitle}>Hantera kategorier</h2>
                <button style={styles.closeButton} onClick={handleCloseCategoryPopup}>
                  <FaTimes />
                </button>
              </div>
              <div style={styles.popupContent}>
                <div style={styles.addCategorySection}>
                  <h3 style={styles.sectionTitle}>
                    <FaPlus /> Lägg till ny kategori
                  </h3>
                  <div style={styles.formGroup}>
                    <input
                      type="text"
                      style={styles.input}
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Skriv in kategori..."
                      onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                    />
                    <button style={styles.addButton} onClick={handleAddCategory}>
                      <FaPlus /> Lägg till
                    </button>
                  </div>
                </div>

                <h3 style={styles.sectionTitle}>
                  <FaTag /> Befintliga kategorier
                </h3>
                <div style={styles.categoryList}>
                  {categories.map((category) => (
                    <div key={category.id} style={styles.categoryItem}>
                      {editingCategory === category.id ? (
                        <>
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                            <input
                              type="text"
                              style={styles.editInput}
                              value={editedCategoryName}
                              onChange={(e) => setEditedCategoryName(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" &&
                                handleEditCategory(category.id, editedCategoryName)
                              }
                              autoFocus
                            />
                            <span
                              style={{
                                ...styles.categoryPreview,
                                ...getCategoryStyle(category.id),
                              }}
                            >
                              Förhandsgranskning
                            </span>
                          </div>
                          <div style={styles.categoryActions}>
                            <button
                              style={{ ...styles.iconButton, ...styles.saveButton }}
                              onClick={() =>
                                handleEditCategory(category.id, editedCategoryName)
                              }
                            >
                              <FaSave /> Spara
                            </button>
                            <button
                              style={{ ...styles.iconButton, ...styles.cancelButton }}
                              onClick={cancelEditing}
                            >
                              <FaTimesCircle /> Avbryt
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={styles.categoryInfo}>
                            <span style={styles.categoryName}>{category.name}</span>
                            <span
                              style={{
                                ...styles.categoryPreview,
                                ...getCategoryStyle(category.id),
                              }}
                            >
                              {category.name}
                            </span>
                          </div>
                          <div style={styles.categoryActions}>
                            <button
                              style={{ ...styles.iconButton, ...styles.editButton }}
                              onClick={() => startEditing(category)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              style={{ ...styles.iconButton, ...styles.deleteButton }}
                              onClick={() => confirmDelete(category)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Borttagningsbekräftelse för kategori */}
        {showDeletePopup && (
          <div style={styles.overlay} onClick={cancelDelete}>
            <div style={styles.simplePopup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.simplePopupText}>
                Ta bort "{categoryToDelete?.name}"?
              </div>
              <div style={styles.simplePopupButtons}>
                <button style={styles.simpleCancelButton} onClick={cancelDelete}>
                  Avbryt
                </button>
                <button style={styles.simpleDeleteButton} onClick={handleDeleteCategory}>
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rapportdetaljer-popup */}
        {selectedReport && (
          <div style={styles.overlay} onClick={handleClosePopup}>
            <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.popupHeader}>
                <h2 style={styles.popupTitle}>Rapportdetaljer</h2>
              <div style={{ display: "flex", gap: "8px" }}>
  <button style={styles.closeButton} onClick={handleClosePopup}>
    <FaTimes />
  </button>
</div>
              </div>
              <div style={styles.popupContent}>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>
                      <FaUser /> Anställd
                    </span>
                    <span style={styles.infoValue}>
                      {selectedReport.employeeName || getEmployeeName(selectedReport.employeeId)}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>
                      <FaTag /> Kategori
                    </span>
                    <span
                      style={{
                        ...styles.infoValue,
                        ...styles.categoryBadge,
                        ...getCategoryStyle(selectedReport.categoryId),
                        display: "inline-block",
                        width: "fit-content",
                      }}
                    >
                      {getCategoryName(selectedReport.categoryId)}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>
                      <FaClock /> Starttid
                    </span>
                    <span style={styles.infoValue}>
                      {formatDateTime(selectedReport.startTime)}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>
                      <FaClock /> Sluttid
                    </span>
                    <span style={styles.infoValue}>
                      {formatDateTime(selectedReport.endTime)}
                    </span>
                  </div>
                </div>

                <div style={styles.notesSection}>
                  <div style={styles.notesLabel}>
                    <FaCommentAlt /> Noteringar
                  </div>
                  <div style={styles.notesContent}>
                    {selectedReport.notes || "Inga noteringar"}
                  </div>
                </div>

                <div style={styles.exportButtons}>
                 <div style={styles.exportButtons}>
  {/* Lägg till TA BORT-knappen först (som röd variant) */}
 <button
  style={{
    ...styles.exportButton,
    background: "#dc2626",
    color: "white",
    border: "none",
  }}
  onClick={() => {
    if (window.confirm("Är du säker på att du vill radera denna rapport? Detta kan inte ångras.")) {
      handleDeleteReport(selectedReport.id);
    }
  }}
>
  <FaTrash /> Radera
</button>
  
</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast-notis */}
        {toastMessage && (
          <div
            style={{
              position: "fixed",
              bottom: 24,
              right: 24,
              background: toastType === "success" ? "#10b981" : "#ef4444",
              color: "white",
              padding: "12px 24px",
              borderRadius: 8,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              zIndex: 2000,
            }}
          >
            {toastMessage}
          </div>
        )}

        <style jsx>{`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
